import { useEffect, useRef, useState } from "react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Button, FOCUS_TARGET, Field, Input, Notice, Textarea } from "../../ui";
import { useNextT } from "../../i18n";
import { useCurrentUser } from "../../bridges/session";
import { useSendContact } from "../api";
import { COUNTER_FROM, localeOf, MESSAGE_LIMIT } from "../paths";
import type { ContactTopic } from "../types";
import { TalkButtons } from "./TalkButtons";
import s from "../contactUs.module.scss";

/** The hidden trap field is never reached by keyboard. */
const HONEYPOT_TAB = -1;

export const MEMBER_TOPICS: ContactTopic[] = ["question", "problem", "idea", "billing", "other"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The page the person came from (never shown, only attached): the referrer's path when it is ours. */
const pageOf = (): string | undefined => {
  try {
    if (!document.referrer) return undefined;
    const u = new URL(document.referrer);
    return u.origin === window.location.origin ? u.pathname : undefined;
  } catch { return undefined; }
};

const useOnline = () => {
  const [on, setOn] = useState(() => typeof navigator === "undefined" || navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
};

type Props = {
  /** member: the signed-in call (the server adds account and role). public: the open call used by the sign-in and sign-up pop-up. */
  mode?: "member" | "public";
  initialTopic?: ContactTopic;
  topics?: ContactTopic[];
  /** Name and e-mail to start with (a signed-in person's own are used when these are absent). */
  defaults?: { name?: string; email?: string };
  /** "Back" on the confirmation. */
  onBack?: () => void;
  /** Called once the message is saved (the page hides its Call / WhatsApp / Text then). */
  onSent?: () => void;
};

/**
 * ONE contact form for the Help area, the locked Owner and (later) the public pages. Topic, name, e-mail, optional phone, message.
 * Account, role, language and page are attached without being mentioned. The text is never lost: a failed send keeps it and offers Try again.
 */
export const ContactForm = ({ mode = "member", initialTopic, topics = MEMBER_TOPICS, defaults, onBack, onSent }: Props) => {
  const { t, lang } = useNextT();
  const user = useCurrentUser();
  const send = useSendContact(mode);
  const online = useOnline();
  const [topic, setTopic] = useState<ContactTopic>(initialTopic && topics.includes(initialTopic) ? initialTopic : topics[0]);
  const [name, setName] = useState(defaults?.name ?? `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim());
  const [email, setEmail] = useState(defaults?.email ?? user?.email ?? "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [trap, setTrap] = useState("");
  const [errors, setErrors] = useState<{ name?: string; email?: string; message?: string }>({});
  const [state, setState] = useState<"idle" | "failed" | "limit">("idle");
  const [sent, setSent] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { if (sent) heading.current?.focus(); }, [sent]);

  const submit = () => {
    const e = {
      name: name.trim() ? undefined : t("contactUs.err_name"),
      email: EMAIL.test(email.trim()) ? undefined : t("contactUs.err_email"),
      message: message.trim() ? undefined : t("contactUs.err_message"),
    };
    setErrors(e);
    if (e.name || e.email || e.message) return;
    setState("idle");
    send.mutate(
      { topic, name: name.trim(), email: email.trim(), phone: phone.trim() || undefined, message: message.trim(), locale: localeOf(lang), page: pageOf(), website: trap },
      {
        onSuccess: () => { setSent(true); onSent?.(); },
        onError: (err: any) => {
          if (err?.status === 429 || err?.code === "LIMIT") setState("limit");
          else if (err?.status === 422 && err?.fieldErrors && Object.keys(err.fieldErrors).length) {
            setErrors({ name: err.fieldErrors.name && t("contactUs.err_name"), email: err.fieldErrors.email && t("contactUs.err_email"), message: err.fieldErrors.message && t("contactUs.err_message") });
          } else setState("failed");
        },
      },
    );
  };

  if (sent) {
    return (
      <section className={s.sent} aria-labelledby="cu-sent">
        <h2 id="cu-sent" ref={heading} tabIndex={-1} className={FOCUS_TARGET}>{t("contactUs.sent.title")}</h2>
        <p>{t("contactUs.sent.body", { email })}</p>
        <div className={s.copy}>
          <span className={s.copyLabel}>{t("contactUs.sent.copy")}</span>
          <strong>{t(`contactUs.topics.${topic}`)}</strong>
          <p>{message.trim()}</p>
        </div>
        <p className={s.note}>{t("contactUs.sent.mailed")}</p>
        {onBack && <div><Button variant="primary" onClick={onBack}>{t("contactUs.back")}</Button></div>}
      </section>
    );
  }

  // A page that has the three buttons on top does not repeat them; the public pop-up has none, so a problem brings them in.
  const talk = mode === "public" && (state === "failed" || state === "limit");
  return (
    <form className={s.form} onSubmit={(ev) => { ev.preventDefault(); submit(); }} noValidate>
      {state === "failed" && <Notice tone="danger" title={t("contactUs.failed_title")}>{t("contactUs.failed_body")}</Notice>}
      {state === "limit" && <Notice tone="warning" title={t("contactUs.limit_title")}>{t("contactUs.limit_body")}</Notice>}
      {talk && <TalkButtons />}
      {!online && <Notice tone="warning" title={t("contactUs.offline_title")}>{t("contactUs.offline_body")}</Notice>}
      <fieldset className={s.topics}>
        <legend>{t("contactUs.topic_label")}</legend>
        {topics.map((id) => (
          <label key={id} className={`${s.topic} ${topic === id ? s.topicOn : ""}`}>
            <input type="radio" name="contact-topic" value={id} checked={topic === id} onChange={() => setTopic(id)} />
            {topic === id && <CheckRoundedIcon aria-hidden />}
            {t(`contactUs.topics.${id}`)}
          </label>
        ))}
      </fieldset>
      <Field label={t("contactUs.name")} error={errors.name}>
        {(f) => <Input id={f.id} aria-describedby={f.describedBy} invalid={f.invalid} value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} />}
      </Field>
      <Field label={t("contactUs.email")} error={errors.email}>
        {(f) => <Input id={f.id} aria-describedby={f.describedBy} invalid={f.invalid} type="email" dir="ltr" value={email} autoComplete="email" onChange={(e) => setEmail(e.target.value)} />}
      </Field>
      <Field label={t("contactUs.phone")} optional={t("contactUs.optional")} hint={t("contactUs.phone_hint")}>
        {(f) => <Input id={f.id} aria-describedby={f.describedBy} type="tel" dir="ltr" value={phone} autoComplete="tel" onChange={(e) => setPhone(e.target.value)} />}
      </Field>
      <Field label={t("contactUs.message")} hint={t("contactUs.message_hint")} error={errors.message}>
        {(f) => <Textarea id={f.id} aria-describedby={f.describedBy} invalid={f.invalid} rows={5} maxLength={MESSAGE_LIMIT} value={message} onChange={(e) => setMessage(e.target.value)} />}
      </Field>
      {message.length >= COUNTER_FROM && <p className={s.counter} aria-live="polite">{message.length.toLocaleString("en-US")} / {MESSAGE_LIMIT.toLocaleString("en-US")}</p>}
      <div className={s.trap} aria-hidden="true">
        <label>Website<input tabIndex={HONEYPOT_TAB} autoComplete="off" name="website" value={trap} onChange={(e) => setTrap(e.target.value)} /></label>
      </div>
      <div className={s.send}>
        <Button variant="primary" type="submit" loading={send.isPending} disabled={!online}>
          {!online ? t("contactUs.waiting") : state === "failed" ? t("contactUs.try_again") : t("contactUs.send")}
        </Button>
        <p className={s.note}>{t("contactUs.answer_note")}</p>
      </div>
    </form>
  );
};
